const hiddenClass = 'selling_plan_theme_integration--hidden';

class SellingPlansWidget {
  constructor(sellingPlansWidgetContainer) {
    this.sellingPlansWidgetContainer = sellingPlansWidgetContainer;
    this.appendSellingPlanInputs();
    this.updateSellingPlanInputsValues();
    this.listenToVariantChange();
    this.listenToSellingPlanFormRadioButtonChange();
    this.listenToPurchaseFrequencyChange();
    this.listToRadioInputChange();
    this.setInitialSelectedSellingPlan();
  }

  listenToPurchaseFrequencyChange() {
    const purchaseFrequencySelects = this.sellingPlansWidgetContainer.querySelectorAll('select.purchase_frequency');
    purchaseFrequencySelects.forEach((select) => {
      const setInitialValue = () => {
      const selectedOptionValue = select.value;
      const parentContainer = select.closest('.subscribe_container');
      const radioInput = parentContainer.querySelector('input[type="radio"]');
      radioInput.dataset.sellingPlanId = selectedOptionValue;
      };
      setInitialValue();
      select.addEventListener('change', (event) => {
        const selectedOptionValue = event.target.value;
        const parentContainer = event.target.closest('.subscribe_container');
        const radioInput = parentContainer.querySelector('input[type="radio"]');
        radioInput.dataset.sellingPlanId = selectedOptionValue;
        purchaseFrequencySelects.forEach((otherSelect) => {
          if (otherSelect !== select) {
            otherSelect.value = selectedOptionValue;
            const otherParentContainer = otherSelect.closest('.subscribe_container');
            const otherRadioInput = otherParentContainer.querySelector('input[type="radio"]');
            otherRadioInput.dataset.sellingPlanId = selectedOptionValue; 
          }
        });
      });
    });
  }

  listToRadioInputChange() {
    const radioInputs = this.sellingPlansWidgetContainer.querySelectorAll('input[type="radio"]');
    radioInputs.forEach(input => {
        input.addEventListener('click', function() {
            if (this.hasAttribute('data-radio-type')) {
                const radioType = this.getAttribute('data-radio-type');
                const relatedInputs = document.querySelectorAll(`input[data-radio-type="${radioType}"]`);
                relatedInputs.forEach(relatedInput => {
                    if (relatedInput !== this) {
                        relatedInput.checked = this.checked;
                    }
                });
            }
        });
    });
  }

  get sectionId() {
    return this.sellingPlansWidgetContainer.getAttribute('data-section-id');
  }

  get shopifySection() {
    return document.querySelector(`#shopify-section-${this.sectionId}`);
  }

  /*
    We are careful to target the correct form, as there are instances when we encounter an installment form that we specifically aim to avoid interacting with.
  */
  get variantIdInput() {
    return (
      this.addToCartForms[1]?.querySelector(`input[name="id"]`) ||
      this.addToCartForms[1]?.querySelector(`select[name="id"]`) ||
      this.addToCartForms[0].querySelector(`input[name="id"]`) ||
      this.addToCartForms[0].querySelector(`select[name="id"]`)
    );
  }

  get visibleSellingPlanForm() {
    return this.shopifySection.querySelector(
      `section[data-variant-id^="${this.variantIdInput.value}"]`,
    );
  }

  get isVariantAvailable() {
    return this.selectedPurchaseOption.getAttributeNames().includes('disabled');
  }

  get sellingPlanInput() {
    return this.shopifySection.querySelector('.selected-selling-plan-id');
  }

  get addToCartForms() {
    return this.shopifySection.querySelectorAll('[data-product-form][action*="/cart/add"]');
  }

  /*
    To enable the addition of a selling plan to a cart, it's necessary to include an input with the name "selling_plan", which will carry the selling ID as its value. When a buyer clicks on 'add to cart', the appropriate selling plan ID is added to their cart.
  */
  appendSellingPlanInputs() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.appendChild(this.sellingPlanInput.cloneNode());
    });
  }

  showSellingPlanForm(sellingPlanFormForSelectedVariant) {
    sellingPlanFormForSelectedVariant?.classList?.remove(hiddenClass);
    sellingPlanFormForSelectedVariant?.classList?.add('active');
  }

  hideSellingPlanForms(sellingPlanFormsForUnselectedVariants) {
    sellingPlanFormsForUnselectedVariants.forEach((element) => {
      element.classList.add(hiddenClass);
      element.classList.remove('active');
    });
  }

  /*
    Each product variant comes with a selling plan selection box that the buyer can interact with.
    When a buyer chooses a different variant, we ensure that only the relevant selling plan selection box is displayed.
    This guarantees that only the selling plan associated with the selected variant is shown.
  */
  handleSellingPlanFormVisibility() {
    const sellingPlanFormForSelectedVariant = this.shopifySection.querySelector(
      `section[data-variant-id="${this.variantIdInput.value}"]`,
    );
    const sellingPlanFormsForUnselectedVariants =
      this.shopifySection.querySelectorAll(
        `.selling_plan_theme_integration:not([data-variant-id="${this.variantIdInput.value}"])`,
      );
    this.showSellingPlanForm(sellingPlanFormForSelectedVariant);
    this.hideSellingPlanForms(sellingPlanFormsForUnselectedVariants);
  }

  handleVariantChange() {
    this.handleSellingPlanFormVisibility();
    this.updateSellingPlanInputsValues();
    this.listenToSellingPlanFormRadioButtonChange();
  }

  /*
    The functions listenToVariantChange() and listenToAddToCartForms() are implemented to track when a product variant is altered or when the product form is updated.
    The identification of the variant is crucial as it dictates which selling plan box should be displayed.
  */
  listenToVariantChange() {
    this.listenToAddToCartForms();
    if (this.variantIdInput.tagName === 'INPUT') {
      const variantIdObserver = new MutationObserver((mutationList) => {
        mutationList.forEach((mutationRecord) => {
          this.handleVariantChange(mutationRecord.target.value);
        });
      });

      variantIdObserver.observe(this.variantIdInput, {
        attributes: true,
      });
    }
  }

  listenToAddToCartForms() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.addEventListener('change', () => {
        this.handleVariantChange();
      });
    });
  }

  get sellingPlanInputs() {
    return this.shopifySection.querySelectorAll('.selected-selling-plan-id');
  }

  updateSellingPlanInputsValues() {
    this.sellingPlanInputs.forEach((sellingPlanInput) => {
      sellingPlanInput.value = this.sellingPlanInputValue;
    });
  }

  get sellingPlanInputValue() {
    return this.selectedPurchaseOption?.dataset.sellingPlanId ?? null;
  }

  get selectedPurchaseOption() {
    return this.visibleSellingPlanForm?.querySelector(
      'input[type="radio"]:checked',
    );
  }

  set selectedPurchaseOption(selectedPurchaseOption) {
    this._selectedPurchaseOption = selectedPurchaseOption;
  }

  handleRadioButtonChange(selectedPurchaseOption) {
    this.selectedPurchaseOption = selectedPurchaseOption;
    this.updateSellingPlanInputsValues();
  }

  listenToSellingPlanFormRadioButtonChange() {
    this.visibleSellingPlanForm
        ?.querySelectorAll('input[type="radio"]')
        .forEach((radio) => {
            radio.addEventListener('change', (event) => {
                this.handleRadioButtonChange(event.target);

                var allocationPrice = event.target.getAttribute('data-allocation-price');
                if (!allocationPrice) {
                    allocationPrice = event.target.getAttribute('data-variant-price');
                    allocationPrice = allocationPrice.replace(/^\$/, '');
                }

                var variantPrice = event.target.getAttribute('data-variant-price');
                var compareAtPrice = event.target.getAttribute('data-variant-compare-at-price') || variantPrice; // Use variantPrice if compareAtPrice is not available
                this.updatePriceAndSavePercentage(allocationPrice, compareAtPrice, variantPrice);
            });
        });
}

updatePriceAndSavePercentage(allocationPrice, compareAtPrice, variantPrice) {
    // Parse the formatted prices to numbers for calculation
    const numericCompareAtPrice = parseFloat(compareAtPrice.replace(/[^0-9.-]+/g, ''));
    const numericAllocationPrice = parseFloat(allocationPrice.replace(/[^0-9.-]+/g, ''));
    const numericVariantPrice = parseFloat(variantPrice.replace(/[^0-9.-]+/g, ''));
    var priceElement = document.getElementById('price_compared_sub_span');
    const hasComparePrice = !!numericCompareAtPrice && numericCompareAtPrice > numericAllocationPrice;
    var savePriceBelow = document.querySelector('.save-price-below');
    if((numericAllocationPrice == numericVariantPrice) & !hasComparePrice){
      priceElement.style.marginRight = "0px";
      savePriceBelow.style.display = 'none';
    }
    else{
      savePriceBelow.style.display = 'block';
      priceElement.style.marginRight = "11px";
    }
   
    
  
    priceElement.style.display = 'block';
    // Update the allocation price display
    allocationPrice = parseFloat(allocationPrice);
    allocationPrice = (allocationPrice % 1 === 0) ? allocationPrice.toFixed(0) : allocationPrice.toFixed(2);
    document.querySelector('.money[data-price]').innerHTML = `$${allocationPrice}`;

    // Update the compare-at price display
    if (hasComparePrice) {
        document.querySelector('.price__compare-at--single-custom').innerHTML = compareAtPrice;
    } else {
        document.querySelector('.price__compare-at--single-custom').innerHTML = ''; // Hide compare-at price if not applicable
    }

    // Update the save percentage display
    if (hasComparePrice) {
        const percentageSaved = parseFloat(((numericCompareAtPrice - numericAllocationPrice) * 100.0 / numericCompareAtPrice).toFixed(2)).toString();
        const savePriceBelow  = document.querySelector('.save-price-below');
        if (savePriceBelow) {
            savePriceBelow.innerHTML = `(Save ${percentageSaved}%)`;
        }
    } else {
        const percentagesSaved = parseFloat(((numericVariantPrice - numericAllocationPrice) * 100.0 / numericVariantPrice).toFixed(2)).toString();
        const savePriceBelow = document.querySelector('.save-price-below');
        if (savePriceBelow) {
            savePriceBelow.innerHTML = `(Save ${percentagesSaved}%)`;
        }
    }
}




  setInitialSelectedSellingPlan() {
    const initialSelectedRadio = this.sellingPlansWidgetContainer.querySelector('input[type="radio"]:checked');
    if (initialSelectedRadio) {
      const selectedPlanId = initialSelectedRadio.dataset.sellingPlanId;
      if (selectedPlanId !== undefined) {
        this.sellingPlanInputs.forEach((sellingPlanInput) => {
          sellingPlanInput.value = selectedPlanId;
        });
      }
    }
  }  
}

document
  .querySelectorAll('.selling_plan_app_container')
  .forEach((sellingPlansWidgetContainer) => {
    new SellingPlansWidget(sellingPlansWidgetContainer);
  });
